import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { BulkPopoverStyled } from '../Bulk-Actions-Bar/styles';
import usePopoverPlacement from '../Bulk-Actions-Bar/usePopoverPlacement';

const DEFAULT_TAGS = ['primary', 'secondary'];

const getInitialStates = (tags, selectedKeywords) => {
  const states = {};

  tags.forEach((tag) => {
    const count = selectedKeywords.filter((keyword) => keyword.tags?.includes(tag)).length;
    states[tag] =
      count === 0 ? 'unchecked' : count === selectedKeywords.length ? 'checked' : 'mixed';
  });

  return states;
};

const PopoverBulkTags = ({ selectedKeywords, accountTags, isSending, onApply, onCancel }) => {
  const { t: translate } = useTranslation();
  const popoverRef = useRef();
  const placement = usePopoverPlacement(popoverRef);
  const searchRef = useRef();
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdTags, setCreatedTags] = useState([]);

  const tags = useMemo(
    () => [
      ...createdTags,
      ...[
        ...new Set([
          ...DEFAULT_TAGS,
          ...accountTags,
          ...selectedKeywords.flatMap((keyword) => keyword.tags ?? []),
        ]),
      ].filter((tag) => !createdTags.includes(tag)),
    ],
    [accountTags, createdTags, selectedKeywords],
  );
  const initialStates = useMemo(
    () => getInitialStates(tags, selectedKeywords),
    [tags, selectedKeywords],
  );
  const [states, setStates] = useState(initialStates);

  useEffect(() => {
    setStates((prevStates) => ({ ...initialStates, ...prevStates }));
  }, [initialStates]);

  const getTagLabel = (tag) => {
    if (tag === 'primary') return translate('Primary');
    if (tag === 'secondary') return translate('Secondary');
    return tag;
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredTags = tags.filter(
    (tag) =>
      !normalizedSearch ||
      tag.toLowerCase().includes(normalizedSearch) ||
      getTagLabel(tag).toLowerCase().includes(normalizedSearch),
  );
  const exactMatch = tags.find(
    (tag) =>
      tag.toLowerCase() === normalizedSearch || getTagLabel(tag).toLowerCase() === normalizedSearch,
  );
  const canCreate = normalizedSearch && !exactMatch;

  const toggleTag = (tag) => {
    setStates((prevStates) => ({
      ...prevStates,
      [tag]: prevStates[tag] === 'checked' ? 'unchecked' : 'checked',
    }));
  };

  const createTag = () => {
    const name = search.trim();

    if (!name) return;

    if (exactMatch) {
      setStates((prevStates) => ({ ...prevStates, [exactMatch]: 'checked' }));
    } else {
      setCreatedTags((prevTags) => [name, ...prevTags]);
      setStates((prevStates) => ({ ...prevStates, [name]: 'checked' }));
    }

    setSearch('');
    setIsCreating(false);
  };

  const startCreating = () => {
    setIsCreating(true);
    searchRef.current?.focus();
  };

  const tagsToAdd = tags.filter(
    (tag) => states[tag] === 'checked' && initialStates[tag] !== 'checked',
  );
  const tagsToRemove = tags.filter(
    (tag) => states[tag] === 'unchecked' && initialStates[tag] !== 'unchecked',
  );
  const hasChanges = tagsToAdd.length > 0 || tagsToRemove.length > 0;

  const selectedCount = selectedKeywords.length;

  return (
    <BulkPopoverStyled
      ref={popoverRef}
      width="18.75rem"
      placement={placement}
      data-figma-id="15935:7799"
    >
      <div className="popover-header" data-figma-id="15935:7800">
        <div className="popover-search" data-figma-id="15935:7801">
          <i className="bx bx-search" />
          <input
            ref={searchRef}
            type="text"
            value={search}
            placeholder={
              isCreating ? translate('Type the new tag name') : translate('Search or create tag')
            }
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                createTag();
              }
            }}
            disabled={isSending}
          />
        </div>
        {isCreating && !normalizedSearch ? (
          <p className="popover-hint popover-hint-active">
            {translate('Type the name and press Enter to create.')}
          </p>
        ) : filteredTags.length === 0 ? (
          <p className="popover-hint">{translate('No tag found with this name.')}</p>
        ) : (
          !normalizedSearch && (
            <p className="popover-hint" data-figma-id="15935:7809">
              {selectedCount === 1
                ? translate('Check to add to the keyword · uncheck to remove')
                : translate('Check to add to the {{count}} keywords · uncheck to remove', {
                    count: selectedCount,
                  })}
            </p>
          )
        )}
      </div>
      {filteredTags.length > 0 && (
        <div className="popover-list">
          {filteredTags.map((tag) => (
            <label
              key={tag}
              className={`popover-item mb-0 ${states[tag] !== 'unchecked' ? 'selected' : ''}`}
            >
              <input
                type="checkbox"
                className="form-check-input"
                checked={states[tag] === 'checked'}
                ref={(element) => {
                  if (element) element.indeterminate = states[tag] === 'mixed';
                }}
                onChange={() => toggleTag(tag)}
                disabled={isSending}
              />
              <span className="popover-item-name">{getTagLabel(tag)}</span>
            </label>
          ))}
        </div>
      )}
      <div className="popover-separator" />
      <div className="popover-create">
        {canCreate ? (
          <button
            type="button"
            className="popover-item selected"
            onClick={createTag}
            disabled={isSending}
          >
            <span className="popover-item-name">
              {translate('+ Create tag “{{name}}”', { name: search.trim() })}
            </span>
            <span className="popover-item-detail">{'Enter ↵'}</span>
          </button>
        ) : (
          <button
            type="button"
            className="popover-item"
            data-figma-id="15935:7833"
            onClick={startCreating}
            disabled={isSending}
          >
            {translate('+ Create new tag')}
          </button>
        )}
      </div>
      <div className="popover-separator" />
      <div className="popover-footer" data-figma-id="15935:7836">
        <button type="button" className="btn-cancel" onClick={onCancel} disabled={isSending}>
          {translate('Cancel')}
        </button>
        <Button
          className="btn-confirm"
          data-figma-id="15935:7838"
          onClick={() => onApply(tagsToAdd, tagsToRemove)}
          disabled={isSending || !hasChanges}
        >
          {isSending ? <i className="bx bx-loader-alt bx-spin" /> : translate('Apply')}
        </Button>
      </div>
    </BulkPopoverStyled>
  );
};

PopoverBulkTags.propTypes = {
  selectedKeywords: PropTypes.array,
  accountTags: PropTypes.array,
  isSending: PropTypes.bool,
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
};

export default PopoverBulkTags;
